import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	FileText,
	ShoppingCart,
	CreditCard,
	Truck,
	Code,
	Shield,
	AlertTriangle,
} from "lucide-react";

const TermsAndConditions = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const sections = [
		{
			icon: <ShoppingCart className="w-8 h-8 text-primary" />,
			title: "Order Acceptance and Cancellation",
			content:
				"When you place an order, you'll get an electronic or other form of order acknowledgement. This is not a guarantee that we've accepted your order or that it's been confirmed. We reserve the right to accept or decline any order for any reason. Sometimes we might need more information from you before we can accept an order.",
		},
		{
			icon: <CreditCard className="w-8 h-8 text-primary" />,
			title: "Payment",
			content:
				"When you're ready to make a purchase, note that all payments must be made through the Site using a valid payment method. You're responsible for any fees or charges associated with your payment method.",
		},
		{
			icon: <Truck className="w-8 h-8 text-primary" />,
			title: "Shipping and Delivery",
			content: (
				<>
					We will use reasonable efforts to deliver your order within the
					estimated delivery time provided (see{" "}
					<Link
						to="/help/shipping-policy"
						className="text-primary hover:text-secondary font-semibold"
					>
						(Shipping Information)
					</Link>{" "}
					for more details). However, we cannot guarantee delivery by a specific
					date, and we will not be liable for any delays in delivery. Title and
					risk of loss for all products will pass to you upon delivery to the
					carrier.
				</>
			),
		},
		{
			icon: <Code className="w-8 h-8 text-primary" />,
			title: "Intellectual Property",
			content:
				"The content and information on the Site, as well as the infrastructure used to provide such content and information, is proprietary to us. You may not use any content or information on the Site for any commercial purpose without the express written consent of Off Cut Shades.",
		},
		{
			icon: <Shield className="w-8 h-8 text-primary" />,
			title: "User Conduct",
			content:
				"You may not damage, disable, overburden, or impair the Site in any manner or interfere with any other party's use of the Site. You may not use the Site to transmit any spam or other unsolicited communications. You may not use the Site to impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity.",
		},
		{
			icon: <AlertTriangle className="w-8 h-8 text-primary" />,
			title: "Disclaimer of Warranties",
			content:
				"The Site is provided on an “as is” and “as available” basis. We make no representations or warranties of any kind, express or implied, as to the operation of the Site or the information, content, materials, or products included on the Site. We do not warrant that the Site will be uninterrupted or error-free, and we will not be held liable for any interruptions or errors.",
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>Terms and Conditions | Off Cut Shades</title>
				<meta
					name="description"
					content="Off Cut Shades terms and conditions. Learn more about our terms here when shopping for custom shades in Canada on our website."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/terms-conditions"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
							<FileText className="w-10 h-10 text-primary" />
						</div>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Terms &{" "}
						<span className="relative">
							<span className="text-primary">Conditions</span>
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-600 max-w-3xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.4 }}
					>
						Please take a moment to read these terms and conditions before using
						the Site or making any purchases.
					</motion.p>
				</motion.div>

				{/* Terms Sections */}
				<div className="space-y-8">
					{sections.map((section, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center m-auto gap-4 mb-6">
									{section.icon}
									<h2 className="text-2xl font-bold text-gray-900">
										{section.title}
									</h2>
								</div>
								<p className="text-gray-600">{section.content}</p>
							</div>
						</motion.div>
					))}
				</div>
			</main>
		</div>
	);
};

export default TermsAndConditions;
